import { CheckCircle2, Circle, Hammer, Loader2, XCircle } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { abortableSleep } from "@/lib/abortable-sleep";
import { cn } from "@/lib/utils";
import {
  generateAppSdd,
  getGenerationStatus,
  PIPELINE_STAGES,
  type PipelineStage,
} from "@/services/debugpavilion";

import { useAiPanelStore, type AiPanelBridge } from "./ai-panel-store";

/** 5 阶段流水线步骤条（当前阶段高亮、已完成打勾）。深色科技风面板（5.4 token）。 */
function PipelineSteps() {
  const craft = useAiPanelStore((state) => state.craft);
  if (!craft) return null;
  const isRunning = craft.status === "running";
  return (
    <div
      data-testid="craft-steps"
      aria-live="polite"
      className={cn(
        "space-y-1.5 rounded-md border border-[var(--border-tech)] bg-[var(--bg-secondary)] p-3",
        isRunning && "shadow-[0_0_16px_-4px_var(--accent-primary)]",
      )}
    >
      {craft.steps.map((step, idx) => {
        const isDone = step.status === "done";
        const isActive = step.status === "active";
        const isError = step.status === "error";
        return (
          <div key={step.id} className="flex items-center gap-2">
            <div className="flex flex-col items-center">
              {isDone ? (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              ) : isError ? (
                <XCircle className="h-4 w-4 text-destructive" />
              ) : isActive ? (
                <Loader2 className="h-4 w-4 animate-spin text-[var(--accent-primary)]" />
              ) : (
                <Circle className="h-4 w-4 text-[var(--text-secondary)]/40" />
              )}
              {idx < craft.steps.length - 1 && <div className="my-0.5 h-3 w-px bg-[var(--border-tech)]" />}
            </div>
            <span
              data-testid={`craft-step-${step.id}`}
              className={cn(
                "text-xs text-[var(--text-primary)]",
                isDone && "text-[var(--text-secondary)] line-through decoration-green-500/60",
                isActive && "font-semibold text-[var(--accent-primary)]",
                isError && "font-semibold text-destructive",
                step.status === "pending" && "text-[var(--text-secondary)]/50",
              )}
            >
              {idx + 1}. {step.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CraftSummary() {
  const craft = useAiPanelStore((state) => state.craft);
  if (!craft || craft.status !== "completed") return null;
  return (
    <div
      data-testid="craft-summary"
      aria-live="polite"
      className="rounded-md border border-green-500/40 bg-green-500/10 p-3 text-xs"
    >
      <div className="flex items-center gap-2 font-semibold text-green-600 dark:text-green-400">
        <CheckCircle2 className="h-4 w-4" />
        构建完成
      </div>
      {craft.reviewScore != null && (
        <div className="mt-1.5 flex items-center justify-between">
          <span className="text-muted-foreground">质量评分</span>
          <span className="font-mono font-semibold text-[var(--accent-secondary)]">
            {(craft.reviewScore * 100).toFixed(0)} / 100
          </span>
        </div>
      )}
      {craft.testResults && (
        <div className="mt-1.5 text-muted-foreground">
          测试结果：
          {typeof craft.testResults === "object" && "summary" in craft.testResults
            ? String((craft.testResults as { summary: unknown }).summary ?? "")
            : JSON.stringify(craft.testResults)}
        </div>
      )}
    </div>
  );
}

export function CraftMode({ savedAppId, onAppCreated }: AiPanelBridge) {
  const craft = useAiPanelStore((state) => state.craft);
  const { startCraft, updateCraftStage, finishCraft, failCraft } = useAiPanelStore();
  const [requirement, setRequirement] = React.useState("");
  // 轮询取消令牌：组件卸载或重新触发时中止在途轮询（含 2s 间隔等待）
  const pollAbortRef = React.useRef<AbortController | null>(null);

  const handleCraft = async () => {
    if (!requirement.trim() || craft?.status === "running") return;
    pollAbortRef.current?.abort();
    const abort = new AbortController();
    pollAbortRef.current = abort;
    try {
      const generation = await generateAppSdd({
        requirement,
        appId: savedAppId ?? undefined,
        sync: false,
      });
      const appId = generation.app_id;
      if (!savedAppId && onAppCreated) onAppCreated(appId);
      startCraft(requirement, generation.id, appId);

      // 轮询 5 阶段流水线进度（pipeline_stage 逐阶段推进）；
      // 间隔等待可被 AbortController 立即中断，卸载后不再轮询
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (abort.signal.aborted) return;
        const record = await getGenerationStatus(generation.id);
        if (abort.signal.aborted) return;
        const stage = record.pipeline_stage;
        if (stage && PIPELINE_STAGES.includes(stage as PipelineStage)) {
          updateCraftStage(stage as PipelineStage);
        }
        if (record.status === "completed") {
          finishCraft({ reviewScore: record.review_score, testResults: record.test_results });
          return;
        }
        if (record.status === "failed") {
          failCraft(record.error_log || "流水线执行失败");
          return;
        }
        await abortableSleep(2000, abort.signal);
      }
    } catch (error) {
      if (abort.signal.aborted) return;
      failCraft(String(error));
    }
  };

  React.useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
    };
  }, []);

  return (
    <div className="flex h-full flex-col">
      <div className="flex-1 space-y-3 overflow-auto p-3">
        <div className="rounded-md bg-[var(--bg-tertiary)] p-2.5 text-xs text-[var(--text-secondary)]">
          构建模式：触发 5 阶段流水线（需求侦察 → 工程基座 → 模块规格 → 并行构建 → 装配质检），全程展示实时进度。
        </div>

        <PipelineSteps />

        <CraftSummary />

        {craft?.status === "failed" && (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive"
            data-testid="craft-error"
            aria-live="polite"
          >
            {craft.error}
          </div>
        )}
      </div>

      <div className="border-t border-[var(--border-tech)] bg-[var(--bg-secondary)] p-3">
        <Textarea
          value={requirement}
          onChange={(e) => setRequirement(e.target.value)}
          placeholder="例如：生成一个完整的客户管理系统，列表、新增表单、搜索与权限..."
          className="min-h-[64px] resize-none text-sm"
        />
        <Button
          className="mt-2 w-full"
          size="sm"
          disabled={craft?.status === "running" || !requirement.trim()}
          onClick={() => void handleCraft()}
          data-testid="craft-generate"
        >
          {craft?.status === "running" ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
          ) : (
            <Hammer className="mr-1.5 h-4 w-4" />
          )}
          {craft?.status === "running" ? "构建中..." : "开始构建"}
        </Button>
      </div>
    </div>
  );
}
